import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  useGetSalesforceConfig,
  useSaveSalesforceConfig,
  useDeleteSalesforceConfig,
  useTestSalesforceConfig,
  getSalesforceStatus,
  getGetSalesforceConfigQueryKey,
  getGetSalesforceStatusQueryKey,
  getCheckSalesforceFieldMappingQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "@/lib/error";
import { toastError } from "@/lib/toast-error";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2,
  ChevronDown,
  Loader2,
  XCircle,
} from "lucide-react";

interface ConnectSalesforceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TestResult {
  success: boolean;
  instanceUrl?: string;
  error?: string;
}

const DEFAULT_LOGIN_URL = "https://login.salesforce.com";

export function ConnectSalesforceDialog({
  open,
  onOpenChange,
}: ConnectSalesforceDialogProps) {
  const queryClient = useQueryClient();
  const { data: currentConfig, isLoading: isLoadingConfig } = useGetSalesforceConfig({
    query: { enabled: open, queryKey: getGetSalesforceConfigQueryKey() },
  });

  const [loginUrl, setLoginUrl] = useState(DEFAULT_LOGIN_URL);
  const [clientId, setClientId] = useState("");
  const [username, setUsername] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mrrField, setMrrField] = useState("");
  const [tcvField, setTcvField] = useState("");
  const [tdmDealIdField, setTdmDealIdField] = useState("");
  const [routingBasisField, setRoutingBasisField] = useState("");
  const [supportedStatesField, setSupportedStatesField] = useState("");
  const [partnerAccountFilter, setPartnerAccountFilter] = useState("");
  const [supplierAccountFilter, setSupplierAccountFilter] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const saveMutation = useSaveSalesforceConfig();
  const testMutation = useTestSalesforceConfig();

  // Initialize form from loaded config whenever the dialog opens or config loads.
  // When the active config is the shared env-var fallback, start with a blank
  // form so the user can enter their own personal credentials to override it.
  useEffect(() => {
    if (!open || !currentConfig || isLoadingConfig) return;
    const isEnv = currentConfig.source === "env";
    setLoginUrl(isEnv ? DEFAULT_LOGIN_URL : currentConfig.loginUrl || DEFAULT_LOGIN_URL);
    setClientId(isEnv ? "" : currentConfig.clientId || "");
    setUsername(isEnv ? "" : currentConfig.username || "");
    setPrivateKey("");
    setMrrField(isEnv ? "MRR__c" : currentConfig.mrrField || "MRR__c");
    setTcvField(isEnv ? "TCV__c" : currentConfig.tcvField || "TCV__c");
    setTdmDealIdField(isEnv ? "TDM_Deal_Id__c" : currentConfig.tdmDealIdField || "TDM_Deal_Id__c");
    setRoutingBasisField(isEnv ? "Routing_Basis__c" : currentConfig.routingBasisField || "Routing_Basis__c");
    setSupportedStatesField(isEnv ? "Supported_States__c" : currentConfig.supportedStatesField || "Supported_States__c");
    setPartnerAccountFilter(isEnv ? "Account.Type = 'Partner'" : currentConfig.partnerAccountFilter || "Account.Type = 'Partner'");
    setSupplierAccountFilter(isEnv ? "Type = 'Supplier'" : currentConfig.supplierAccountFilter || "Type = 'Supplier'");
  }, [open, currentConfig, isLoadingConfig]);

  function resetForm() {
    setLoginUrl(DEFAULT_LOGIN_URL);
    setClientId("");
    setUsername("");
    setPrivateKey("");
    setMrrField("");
    setTcvField("");
    setTdmDealIdField("");
    setRoutingBasisField("");
    setSupportedStatesField("");
    setPartnerAccountFilter("");
    setSupplierAccountFilter("");
    setTestResult(null);
  }

  function handleClose() {
    resetForm();
    onOpenChange(false);
  }

  async function handleTest() {
    if (!loginUrl || !clientId || !username || !privateKey) {
      toast.error("Fill in all required fields before testing");
      return;
    }
    setIsTesting(true);
    setTestResult(null);
    testMutation.mutate(
      {
        data: {
          loginUrl,
          clientId,
          username,
          privateKey,
          ...(mrrField ? { mrrField } : {}),
          ...(tcvField ? { tcvField } : {}),
          ...(tdmDealIdField ? { tdmDealIdField } : {}),
          ...(routingBasisField ? { routingBasisField } : {}),
          ...(supportedStatesField ? { supportedStatesField } : {}),
          ...(partnerAccountFilter ? { partnerAccountFilter } : {}),
          ...(supplierAccountFilter ? { supplierAccountFilter } : {}),
        },
      },
      {
        onSuccess: (result) => {
          setTestResult(result);
          setIsTesting(false);
        },
        onError: (err: unknown) => {
          setTestResult({ success: false, error: getErrorMessage(err, "Test failed") });
          setIsTesting(false);
        },
      },
    );
  }

  function handleSave() {
    if (!loginUrl || !clientId || !username) {
      toast.error("Fill in all required fields");
      return;
    }
    if (!privateKey && !isConfigured) {
      toast.error("Private key is required for a new connection");
      return;
    }
    setIsTesting(true);
    setTestResult(null);
    saveMutation.mutate(
      {
        data: {
          loginUrl,
          clientId,
          username,
          privateKey: privateKey || "",
          ...(mrrField ? { mrrField } : {}),
          ...(tcvField ? { tcvField } : {}),
          ...(tdmDealIdField ? { tdmDealIdField } : {}),
          ...(routingBasisField ? { routingBasisField } : {}),
          ...(supportedStatesField ? { supportedStatesField } : {}),
          ...(partnerAccountFilter ? { partnerAccountFilter } : {}),
          ...(supplierAccountFilter ? { supplierAccountFilter } : {}),
        },
      },
      {
        onSuccess: async () => {
          // Verify the saved credentials actually work before closing
          try {
            const status = await getSalesforceStatus();
            setIsTesting(false);
            if (status.connected) {
              setTestResult({ success: true, instanceUrl: status.instanceUrl ?? undefined });
              queryClient.invalidateQueries({ queryKey: getGetSalesforceConfigQueryKey() });
              queryClient.invalidateQueries({ queryKey: getGetSalesforceStatusQueryKey() });
              queryClient.invalidateQueries({ queryKey: getCheckSalesforceFieldMappingQueryKey() });
              toast.success(isConfigured ? "Salesforce credentials updated" : "Salesforce connected successfully");
              handleClose();
            } else {
              setTestResult({ success: false, error: status.error ?? "Credentials saved but connection could not be verified. Check your credentials and try again." });
            }
          } catch {
            setIsTesting(false);
            setTestResult({ success: false, error: "Credentials saved but could not verify connection. Check the server logs." });
          }
        },
        onError: (err: unknown) => {
          setIsTesting(false);
          toastError(err, "Failed to save credentials");
        },
      },
    );
  }

  const isEnvConfig = currentConfig?.source === "env";
  // "isConfigured" here means the *user* has a personal saved config — when
  // they're on the shared env-var fallback, treat the form as a fresh
  // "connect" (private key required, button labelled "Connect").
  const isConfigured = Boolean(currentConfig?.configured && currentConfig?.source === "db");
  const canSave = Boolean(
    loginUrl && clientId && username && (privateKey || isConfigured)
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Salesforce</DialogTitle>
          <DialogDescription>
            Enter your Salesforce Connected App credentials to enable the integration.
            {isEnvConfig && (
              <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">
                You're currently using the shared organization Salesforce connection (set via environment variables). Saving credentials here will create a personal connection that overrides the shared one for your account only.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoadingConfig ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="sf-login-url">
                Login URL
              </Label>
              <Input
                id="sf-login-url"
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
                placeholder="https://login.salesforce.com"
              />
              <p className="text-xs text-muted-foreground">
                Use <code className="font-mono">https://test.salesforce.com</code> for sandboxes.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sf-client-id">
                Consumer Key (Client ID) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sf-client-id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="3MVG9…"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sf-username">
                Integration Username <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sf-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="integration@yourorg.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sf-private-key">
                Private Key (PEM){!isConfigured && <span className="text-destructive"> *</span>}
              </Label>
              <Textarea
                id="sf-private-key"
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder={isConfigured ? "Leave blank to keep the existing private key" : "-----BEGIN RSA PRIVATE KEY-----\n…\n-----END RSA PRIVATE KEY-----"}
                rows={6}
                className="font-mono text-xs resize-none"
              />
              {isConfigured && (
                <p className="text-xs text-muted-foreground">
                  The private key is stored encrypted. Leave this blank to keep the current key.
                </p>
              )}
            </div>

            {testResult && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${testResult.success ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300" : "bg-destructive/5 border-destructive/20 text-destructive"}`}>
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                )}
                <div>
                  {testResult.success ? (
                    <>
                      <span className="font-medium">Connection successful!</span>
                      {testResult.instanceUrl && (
                        <span className="block text-xs mt-0.5 font-mono opacity-80">{testResult.instanceUrl}</span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Connection failed</span>
                      {testResult.error && (
                        <span className="block text-xs mt-0.5 opacity-80">{testResult.error}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={handleTest}
              disabled={!canSave || isTesting || testMutation.isPending}
            >
              {(isTesting || testMutation.isPending) ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Test Connection
            </Button>

            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                  Advanced field overrides
                  <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                <p className="text-xs text-muted-foreground">
                  Override the Salesforce API field names used by this integration. Leave blank to use defaults.
                </p>
                {[
                  { label: "MRR Field", id: "sf-mrr", value: mrrField, set: setMrrField, placeholder: "MRR__c" },
                  { label: "TCV Field", id: "sf-tcv", value: tcvField, set: setTcvField, placeholder: "TCV__c" },
                  { label: "TDM Deal ID Field", id: "sf-tdm-deal-id", value: tdmDealIdField, set: setTdmDealIdField, placeholder: "TDM_Deal_Id__c" },
                  { label: "Routing Basis Field (Account)", id: "sf-routing-basis", value: routingBasisField, set: setRoutingBasisField, placeholder: "Routing_Basis__c" },
                  { label: "Supported States Field (Contact)", id: "sf-supported-states", value: supportedStatesField, set: setSupportedStatesField, placeholder: "Supported_States__c" },
                  { label: "Partner Account Filter", id: "sf-partner-filter", value: partnerAccountFilter, set: setPartnerAccountFilter, placeholder: "Account.Type = 'Partner'" },
                  { label: "Supplier Account Filter", id: "sf-supplier-filter", value: supplierAccountFilter, set: setSupplierAccountFilter, placeholder: "Type = 'Supplier'" },
                ].map(({ label, id, value, set, placeholder }) => (
                  <div key={id} className="space-y-1">
                    <Label htmlFor={id} className="text-xs">{label}</Label>
                    <Input
                      id={id}
                      value={value}
                      onChange={(e) => set(e.target.value)}
                      placeholder={placeholder}
                      className="h-8 text-sm font-mono"
                    />
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || saveMutation.isPending || isTesting}
              >
                {(saveMutation.isPending || isTesting) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saveMutation.isPending
                  ? "Saving..."
                  : isTesting
                  ? "Verifying connection..."
                  : isConfigured
                  ? "Update Credentials"
                  : isEnvConfig
                  ? "Save personal connection"
                  : "Connect Salesforce"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DisconnectSalesforceButtonProps {
  onDisconnected: () => void;
}

export function DisconnectSalesforceButton({ onDisconnected }: DisconnectSalesforceButtonProps) {
  const disconnectMutation = useDeleteSalesforceConfig();
  const queryClient = useQueryClient();

  function handleDisconnect() {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success("Salesforce disconnected");
        queryClient.invalidateQueries({ queryKey: getGetSalesforceConfigQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetSalesforceStatusQueryKey() });
        onDisconnected();
      },
      onError: (err: unknown) => {
        toastError(err, "Failed to disconnect Salesforce");
      },
    });
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={disconnectMutation.isPending}
        >
          {disconnectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Disconnect
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect Salesforce?</AlertDialogTitle>
          <AlertDialogDescription>
            This will remove the stored Salesforce credentials. Import from Salesforce,
            push to Salesforce, supplier directory, and channel-manager search will stop
            working until you reconnect.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDisconnect}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Disconnect
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
